import { useEffect, useState } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';

import { gtmBasicEvent } from '@klv/shared/utils/google-tag-manager';
import { useAuthentication } from '@klv/context/authentication';
import { generateClassName, windowIsDefined } from '@klv/shared/utils/functions';

import { Ampersand } from './children/ampersand';
import { Arrow } from './children/arrow';
import { Select } from './children/select';
import { Weeble } from './children/weeble';

import * as styles from './styles/bfcmHero.module.css';

import type { ReactElement } from 'react';
import type { IBfcmHero } from './types';

const isUkLocale = () => windowIsDefined() && window.location.href.includes('/uk/');

const AUDIENCE_OPTIONS = [
  { value: 'customer', label: 'a Klaviyo customer' },
  { value: 'partner', label: 'a Klaviyo partner' },
  { value: 'prospect', label: 'curious about Klaviyo' },
];

const AUDIENCE_VALUES = AUDIENCE_OPTIONS.map(({ value }) => value);

const CUSTOMER_PROSPECT_GOAL_OPTIONS = [
  { value: 'grow', label: 'grow my audience quickly' },
  { value: 'create', label: 'create targeted messages' },
  { value: 'coordinate', label: 'coordinate email and SMS' },
  { value: 'work', label: 'work faster and smarter with AI' },
  { value: 'try', label: 'try new features and tactics' },
  { value: 'sell', label: 'sell more efficiently' },
];
const CUSTOMER_PROSPECT_GOAL_VALUES = CUSTOMER_PROSPECT_GOAL_OPTIONS.map(({ value }) => value);

const PARTNER_GOAL_OPTIONS = [
  { value: 'maximize', label: isUkLocale() ? 'maximise customer acquisition' : 'maximize customer acquisition' },
  { value: 'optimize', label: isUkLocale() ? 'optimise campaign performance' : 'optimize campaign performance' },
  { value: 'grow', label: 'grow my agency with Klaviyo' },
  { value: 'leverage', label: 'leverage AI with Klaviyo' },
  { value: 'retain', label: 'retain client accounts' },
  { value: 'drive', label: 'drive client revenue growth' },
];

const PARTNER_GOAL_VALUES = PARTNER_GOAL_OPTIONS.map(({ value }) => value);

const getAudienceFromParams = () => {
  const url = new URL(window.location.href);

  if (url.searchParams.has('audience')) {
    const audienceParam = url.searchParams.get('audience');
    if (audienceParam && AUDIENCE_VALUES.includes(audienceParam)) {
      return audienceParam;
    }
  }

  return '';
};

const renderComponents = (components: ReactElement[], goal: string) => {
  const featured = components.find(({ props }) => props?.headline?.toLowerCase()?.startsWith(goal));

  return (
    <>
      {featured}
      {components.filter((Component) => Component !== featured).map((Component) => Component)}
    </>
  );
};

export const BfcmHero = ({
  className = '',
  customerComponents,
  partnerComponents,
  prospectComponents,
  ...props
}: IBfcmHero) => {
  const { t } = useTranslation();
  const [audience, setAudience] = useState('prospect');
  const [goal, setGoal] = useState('grow');

  const { authenticationState: { loggedIn } } = useAuthentication();

  const GOAL_OPTIONS = audience === 'partner' ? PARTNER_GOAL_OPTIONS : CUSTOMER_PROSPECT_GOAL_OPTIONS;
  const GOAL_VALUES = audience === 'partner' ? PARTNER_GOAL_VALUES : CUSTOMER_PROSPECT_GOAL_VALUES;

  useEffect(() => {
    if (!GOAL_VALUES.includes(goal)) {
      setGoal(GOAL_VALUES[0]);
    }
  }, [audience]);

  useEffect(() => {
    if (loggedIn) {
      const audienceParam = getAudienceFromParams();

      if (!audienceParam) {
        setAudience('customer');
      }
    }
  }, [loggedIn]);

  useEffect(() => {
    const audienceParam = getAudienceFromParams();

    if (audienceParam) {
      setAudience(audienceParam);
    }
  }, []);

  useEffect(() => {
    gtmBasicEvent({
      action: [audience, goal],
      non_interaction_flag: false,
      category: 'bfcm_hero',
    });
  }, [audience, goal]);

  return (
    <>
      <div className={generateClassName([className, styles.bfcmHero])} {...props}>
        <div className={generateClassName([styles.container, 'grid'])}>
          <h1 className={styles.textContent}>
            <span className={styles.eyebrow}>{t('organisms_bfcmHero_eyebrow')}</span>
            <span className={styles.headline}>{t('organisms_bfcmHero_headline')}</span>
          </h1>
          <div className={styles.selectorContent}>
            <Weeble />
            <span className={styles.headlineFilterText}>{t('organisms_bfcmHero_headlineFilter_iAm')}</span>
            <Select options={AUDIENCE_OPTIONS} value={audience} onChange={setAudience} />
            <Ampersand className={generateClassName([styles.headlineFilterText, styles.ampersand])} />
            <span className={styles.headlineFilterText}>{t('organisms_bfcmHero_headlineFilter_thisBfcm')}</span>
            <Weeble variant='two' />
            <span className={styles.headlineFilterText}>{t('organisms_bfcmHero_headlineFilter_iWantTo')}</span>
            <Select options={GOAL_OPTIONS} value={goal} onChange={setGoal} selectClassName={styles.goalSelector} />
            <Weeble variant='three' />
          </div>
          <div className={styles.arrowContent}>
            <a href="#bfcm-content" aria-label='Continue to content'>
              <Arrow className={styles.arrow} />
            </a>
          </div>
        </div>
      </div>
      <div className={styles.audienceContent} id='bfcm-content'>
        {audience === 'customer' && customerComponents && renderComponents(customerComponents, goal)}
        {audience === 'partner' && partnerComponents && renderComponents(partnerComponents, goal)}
        {audience === 'prospect' && prospectComponents && renderComponents(prospectComponents, goal)}
      </div>
    </>
  );
};
